import type { PartnerOpportunityRequestResponse, PartnerPortalReadBundle, PartnerType, PartnerUserLinkRole } from "../../types/revenueOps";

export type PartnerPortalLinkedPartner = {
  id: string;
  display_name: string;
  partner_type: PartnerType;
  access_role?: PartnerUserLinkRole;
};

export type PartnerPortalClientResponse = {
  bundle: PartnerPortalReadBundle;
  linked_partners: PartnerPortalLinkedPartner[];
};

export class PartnerPortalClientError extends Error {
  status: number;
  phase?: string;

  constructor(status: number, message: string, phase?: string) {
    super(message);
    this.name = "PartnerPortalClientError";
    this.status = status;
    this.phase = phase;
  }
}

export async function loadPartnerPortalReadModel(accessToken: string, partnerId?: string): Promise<PartnerPortalClientResponse> {
  const query = partnerId ? `?partner_id=${encodeURIComponent(partnerId)}` : "";
  const response = await fetch(`/api/revenue-ops-partner-portal${query}`, { headers: { Authorization: `Bearer ${accessToken}` } });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new PartnerPortalClientError(response.status, body.message || body.error || "Partner dashboard data could not be loaded", body.phase);
  return body as PartnerPortalClientResponse;
}

async function postPartnerPortalAction<T>(accessToken: string, partnerId: string, action: string, payload: Record<string, unknown>): Promise<T> {
  const response = await fetch("/api/revenue-ops-partner-portal", { method: "POST", headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" }, body: JSON.stringify({ action, selected_partner_id: partnerId, ...payload }) });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new PartnerPortalClientError(response.status, body.message || body.error || "Partner request could not be submitted", body.phase);
  return body as T;
}

export function submitPartnerOpportunity(accessToken: string, partnerId: string, input: { prospect_name: string; parent_entity?: string; website?: string; category?: string; notes?: string; expected_close_date?: string }): Promise<PartnerOpportunityRequestResponse> {
  return postPartnerPortalAction<PartnerOpportunityRequestResponse>(accessToken, partnerId, "submit_opportunity", input);
}

export function requestPartnerOpportunityClaim(accessToken: string, partnerId: string, opportunityId: string): Promise<PartnerOpportunityRequestResponse> {
  return postPartnerPortalAction<PartnerOpportunityRequestResponse>(accessToken, partnerId, "request_claim", { opportunity_id: opportunityId });
}
